import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import { Button, Divider, Icon, IconButton, List, ListItem, ListItemButton, Menu, MenuItem, paperClasses, Popover, Stack, Typography } from '@mui/material';
import { Box } from '@mui/system';

import isEmpty from 'Lib/utilities/isEmpty';
import { getRecentlyVisited } from 'Lib/utilities/recentlyVisited';
import { screenSize } from 'Lib/utilities/screenSize';
import userDevice from 'Lib/utilities/userDevice';

/**
 * Renders a dropdown menu with a list of actions.
 *
 * @param {Object[]} actions - The list of actions to be rendered in the dropdown menu.
 * @param {HTMLElement} anchorEl - The anchor element that the dropdown menu is attached to.
 * @param {Object} history - The history object from react-router.
 * @param {boolean} open - Indicates whether the dropdown menu is open or not.
 * @param {function} handleClick - The function to handle the click event on the dropdown button.
 * @param {function} handleClose - The function to handle the close event of the dropdown menu.
 * @returns {JSX.Element} - The rendered dropdown menu component.
 */
const DropdownActions = ({ actions, anchorEl, history, open, handleClick, handleClose }) => (
	<>
		<Button
			aria-haspopup="true"
			aria-controls={open ? 'actions-menu' : undefined}
			aria-expanded={open ? 'true' : undefined}
			endIcon={!open ? <Icon>expand_more</Icon> : <Icon>expand_less</Icon>}
			onClick={handleClick}
			variant="primary"
			sx={theme => ({
				borderRadius: 10,
				fontSize: theme.typography.pxToRem(16),
				width: '122px',
				height: '40px'
			})}>
			Actions
		</Button>
		<Menu
			id="actions-menu"
			open={open}
			anchorEl={anchorEl}
			onClose={handleClose}
			MenuListProps={{
				'aria-labelledby': 'action-button'
			}}
			anchorOrigin={{
				vertical: 'bottom',
				horizontal: 'right'
			}}
			transformOrigin={{
				vertical: 'top',
				horizontal: 'right'
			}}>
			{actions
				.filter(action => action)
				.map(({ label, to, onClick, disabled = false, divider }, index) => (
					// Menu doesn't accept fragments
					<div key={index}>
						{divider ? (
							<Divider sx={{ borderColor: 'common.black' }} />
						) : (
							<MenuItem
								sx={{ py: 3 }}
								disabled={disabled}
								data-pendo-selector={label}
								onClick={e => {
									if (onClick) {
										onClick(e);
									} else {
										history.push(to);
									}
									handleClose();
								}}>
								{label}
							</MenuItem>
						)}
					</div>
				))}
		</Menu>
	</>
);
/**
 * Renders a single action button.
 *
 * @param {Object} actions - The actions object containing the action details.
 * @param {Object} history - The history object from react-router.
 * @returns {JSX.Element} - The rendered single action button.
 */
const SingleAction = ({ actions, history }) => {
	// mobileLabel is destructured to aviod html warnings
	const { label, mobileLabel, icon, to, onClick, sx, ...rest } = actions[0];

	return (
		<Button
			{...rest}
			variant="primary"
			aria-label={label}
			onClick={e => (onClick ? onClick(e) : history.push(to))}
			startIcon={icon && <Icon sx={{ fontSize: '25px !important' }}>{icon}</Icon>}
			sx={theme => ({
				height: 40,
				fontSize: theme.typography.pxToRem(16),
				borderRadius: '40px',
				...sx
			})}>
			{label}
		</Button>
	);
};

/**
 Subtitle Component that shows the 5 most recently visited pages allowing for quick navigation
 along with an Input component related to the View to search for a specific Property, Unit, Lease, Work Order, etc.

 @component
 @param {Element} anchorEl - The anchor element for the popover.
 @param {object} history - The history object provided by React Router.
 @param {object} subtitle - Contains the following:
  {
  label: string || React Component, (required) - The text you'll see below the title of the page itself.
  title: string, (required) - The title of the popover.
  Input: React Component (required) - The Input component used to search, as mentioned above.
  }
 @param {boolean} open - Determines if the popover is open or closed.
 @param {Function} onClick - The click event handler for expanding the content.
 @param {Function} onClose - The callback function called when the popover is closed.
 @returns {JSX.Element} The rendered Subtitle component.
 **/
const Subtitle = ({ anchorEl, history, historyKey, subtitle, open, onClick, onClose }) => {
	const { title, label, historyTypeID, Input } = subtitle;
	const isComponent = typeof label !== 'string';

	// Gets the 5 most recently visited pages
	const recentlyVisited = getRecentlyVisited(historyKey, historyTypeID, 5);

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				height: 24,
				mt: '12px !important'
			}}>
			{isComponent ? (
				label
			) : (
				<Typography
					sx={{
						fontSize: theme => theme.typography.pxToRem(16),
						lineHeight: theme => theme.typography.pxToRem(28)
					}}>
					{label}
				</Typography>
			)}
			{historyTypeID && (
				<>
					<IconButton onClick={onClick} sx={{ ml: 2 }}>
						expand_more
					</IconButton>
					<Popover
						id="recently-visited-popover"
						open={open}
						anchorEl={anchorEl}
						onClose={onClose}
						anchorOrigin={{
							vertical: 'bottom',
							horizontal: 'left'
						}}
						transformOrigin={{
							vertical: 'top',
							horizontal: 'center'
						}}
						sx={theme => ({
							[theme.breakpoints.down('lg')]: {
								[`& .${paperClasses.root}`]: { width: 298 }
							}
						})}>
						<Box sx={{ pt: 4, px: 4, minWidth: 298 }}>
							<Typography
								sx={{
									pb: 4,
									fontSize: theme => theme.typography.pxToRem(20),
									lineHeight: theme => theme.typography.pxToRem(28)
								}}>
								{title}
							</Typography>
							<Input />
						</Box>
						<List disablePadding sx={{ py: 2 }}>
							{recentlyVisited.map(({ label, path }, index) => (
								<ListItem key={index} disableGutters disablePadding>
									<ListItemButton onClick={() => history.push(path)}>{label}</ListItemButton>
								</ListItem>
							))}
						</List>
					</Popover>
				</>
			)}
		</Box>
	);
};
/**
 * Renders the header title component.
 *
 * @param {ReactNode} children - The content to be displayed as the header title.
 * @returns {ReactNode} The rendered header title component.
 */
export const HeaderTitle = ({ children }) => {
	const { mobile } = userDevice();

	return (
		<Typography
			component="h1"
			variant="titleLarge"
			data-cy="page-header-mui"
			sx={{
				fontSize: theme => theme.typography.pxToRem(mobile ? 22 : 32),
				lineHeight: theme => theme.typography.pxToRem(mobile ? 28 : 40),
				ml: 2,
				width: 'auto',
				height: 'auto',
				textTransform: 'none'
			}}>
			{children}
		</Typography>
	);
};

/**
 * Renders the header component with optional breadcrumbs and icon.
 *
 * @param {Array} breadcrumbs - The array of breadcrumb objects.
 * @param {string} icon - The icon to be displayed.
 * @param {string} backButton - A back button displayed at the top of the page.
 * @param {string|JSX.Element} title - The title to be displayed.
 * @param {boolean} showIcon - Determines whether to show the icon or not.
 * @param {Object} history - The history object from react-router.
 * @param {boolean} isMobileDevice - Determines if the device is mobile or not.
 * @param {boolean} menuOpen - Determines if the menu is open or not.
 * @param {function} handleMenuClick - The function to handle menu click event.
 * @param {HTMLElement} menuAnchorEl - The anchor element for the menu.
 * @param {function} handleMenuClose - The function to handle menu close event.
 * @returns {JSX.Element} The rendered header component.
 */
const Header = ({ breadcrumbs, icon, backButton, title, showIcon, history, isMobileDevice, menuOpen, handleMenuClick, menuAnchorEl, handleMenuClose }) => {
	const isComponent = typeof title !== 'string';

	return (
		<>
			<Box display="flex" alignItems="center">
				{showIcon ? (
					<Icon
						sx={{
							fontSize: theme => theme.typography.pxToRem(isMobileDevice ? 22 : 32)
						}}>
						{icon}
					</Icon>
				) : null}
				{backButton && (
					<IconButton
						component={Link}
						to={backButton}
						sx={{
							fontSize: theme => theme.typography.pxToRem(48),
							fontWeight: '700',
							ml: '-15px',
							mr: '-5px'
						}}>
						chevron_left
					</IconButton>
				)}
				{isComponent ? title : <HeaderTitle>{title}</HeaderTitle>}
			</Box>
			{breadcrumbs && !isEmpty(breadcrumbs.pages) && (
				<>
					<IconButton
						id="breadcrumbs-button"
						aria-controls={menuOpen ? 'breadcrumbs-menu' : undefined}
						aria-haspopup="true"
						aria-expanded={menuOpen ? 'true' : undefined}
						onClick={handleMenuClick}>
						{menuOpen ? 'expand_less' : 'expand_more'}
					</IconButton>
					<Menu
						id="breadcrumbs-menu"
						open={menuOpen}
						anchorEl={menuAnchorEl}
						onClose={handleMenuClose}
						MenuListProps={{
							'aria-labelledby': 'breadcrumbs-button'
						}}>
						<Typography
							sx={{
								fontSize: '10px',
								fontWeight: 'regular',
								textTransform: 'uppercase',
								letterSpacing: '1.5px',
								ml: 3,
								mt: 2,
								width: '100%'
							}}>
							{breadcrumbs.header}
						</Typography>
						{breadcrumbs.pages.map(({ title, url }, index) => (
							<MenuItem key={index} component={Link} to={url} underline="none" color="inherit">
								<Typography>{title}</Typography>
							</MenuItem>
						))}
						<Divider />
						<MenuItem onClick={() => history.push('/settings')} sx={{ py: 4, px: 3 }}>
							<Icon mr={2}>arrow_back</Icon>
							Back to Settings
						</MenuItem>
					</Menu>
				</>
			)}
		</>
	);
};

/**
 * Component representing a page header.
 *
 * @param {Array<Object>} actions - Actions to be displayed in the header.
 * @param {Array<Object>} breadcrumbs - Breadcrumbs to be displayed in the header.
 * @param {string} icon - Icon to be displayed in the header.
 * @param {string} subtitle - Subtitle to be displayed in the header.
 * @param {string} title - Title to be displayed in the header.
 * @returns {JSX.Element} - The rendered page header component.
 */
export default function PageHeader({ actions, breadcrumbs, icon, subtitle, title, backButton }) {
	const history = useHistory();
	const app = useSelector(state => state.app);
	const { mobile: isMobileDevice } = userDevice();
	const { mobile } = screenSize();

	const [actionsAnchor, setActionAnchor] = useState(null);
	const [subtitleAnchor, setSubtitleAnchor] = useState(null);
	const [menuAnchorEl, setMenuAnchorEl] = useState(null);

	const showIcon = icon && !breadcrumbs;

	const menuOpen = Boolean(menuAnchorEl);
	const isActionDropdownOpen = Boolean(actionsAnchor);
	const isSubtitlePopoverOpen = Boolean(subtitleAnchor);

	const handleActionsClick = event => {
		setActionAnchor(event.currentTarget);
	};

	const handleActionsClose = () => {
		setActionAnchor(null);
	};

	const handleSubtitleClick = event => {
		setSubtitleAnchor(event.currentTarget);
	};

	const handleSubtitleClose = () => {
		setSubtitleAnchor(null);
	};

	const handleMenuClick = event => {
		setMenuAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setMenuAnchorEl(null);
	};

	const HeaderProps = {
		breadcrumbs,
		icon,
		backButton,
		title,
		showIcon,
		history,
		isMobileDevice,
		menuOpen,
		menuAnchorEl,
		handleMenuClick,
		handleMenuClose
	};

	const historyKey = `${app.portalTypeID}_${app.account?.accountID}_${app.user?.userID}`;

	return (
		<Box component="header" sx={{ m: 4, display: 'flex', justifyContent: 'space-between' }}>
			<Box
				sx={{
					display: 'flex',
					alignSelf: 'center',
					alignItems: 'center',
					flexGrow: '1',
					height: 'auto',
					gap: 2
				}}>
				{subtitle ? (
					<Stack spacing={1}>
						<Header {...HeaderProps} />
						<Subtitle
							historyKey={historyKey}
							anchorEl={subtitleAnchor}
							history={history}
							open={isSubtitlePopoverOpen}
							subtitle={subtitle}
							onClick={handleSubtitleClick}
							onClose={handleSubtitleClose}
						/>
					</Stack>
				) : (
					<Header {...HeaderProps} />
				)}
			</Box>

			{!isEmpty(actions) && !mobile && (
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'flex-end',
						height: 'fit-content'
					}}>
					{actions.render ? (
						actions.render()
					) : actions.length > 1 ? (
						<DropdownActions
							actions={actions}
							anchorEl={actionsAnchor}
							history={history}
							open={isActionDropdownOpen}
							handleClick={handleActionsClick}
							handleClose={handleActionsClose}
						/>
					) : (
						<SingleAction actions={actions} history={history} />
					)}
				</Box>
			)}
			{/* Portal container that is used inside of <MuiTable /> */}
			{mobile && <div id="table-view-controls" />}
		</Box>
	);
}
